import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, Input, SearchableSelect, InputAmount, Modal } from '@/backoffice/components/ui';
import { formatCurrency, formatDate, formatTime } from '@/backoffice/utils';
import {
  fetchDriverTrips,
  createDriverTrip,
  deleteDriverTrip,
  type DriverTrip,
} from '@/domains/driver/services/driver.service';
import { fetchZones } from '@/domains/admin/services/admin.service';
import { Route, Plus, Calendar, Package, Trash2, Loader2 } from 'lucide-react';

const emptyForm = {
  departure_city: '',
  arrival_city: '',
  departure_date: '',
  departure_time: '08:00',
  available_space: 0,
  price_per_kg: 500,
};

export function CommunityTripsPage() {
  const [trips, setTrips] = useState<DriverTrip[]>([]);
  const [zones, setZones] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewTripForm, setShowNewTripForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetchDriverTrips(),
      fetchZones().then((list) => list.map((z) => ({ value: z.name, label: z.name }))),
    ])
      .then(([tripList, zoneOptions]) => {
        setTrips(tripList);
        setZones(zoneOptions);
      })
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.departure_city || !form.arrival_city || !form.departure_date) return;

    setSaving(true);
    try {
      const departureDate = new Date(`${form.departure_date}T${form.departure_time}`);
      await createDriverTrip({
        departure_city: form.departure_city,
        arrival_city: form.arrival_city,
        departure_date: departureDate.toISOString(),
        available_space: form.available_space,
        price_per_kg: form.price_per_kg,
      });
      setForm(emptyForm);
      setShowNewTripForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await deleteDriverTrip(id);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="community_driver" title="Mes trajets">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="community_driver" title="Mes trajets">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          Planifiez vos trajets pour accepter des colis de vos voisins
        </p>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowNewTripForm(true)}>
          Nouveau trajet
        </Button>
      </div>

      <Modal isOpen={showNewTripForm} onClose={() => setShowNewTripForm(false)} title="Nouveau trajet" size="lg">
        <div className="grid md:grid-cols-2 gap-4">
          <SearchableSelect
            label="Ville de départ"
            options={zones}
            value={form.departure_city}
            onChange={(v) => setForm((f) => ({ ...f, departure_city: v }))}
            placeholder="Sélectionner"
          />
          <SearchableSelect
            label="Ville d'arrivée"
            options={zones}
            value={form.arrival_city}
            onChange={(v) => setForm((f) => ({ ...f, arrival_city: v }))}
            placeholder="Sélectionner"
          />
          <Input
            label="Date de départ"
            type="date"
            value={form.departure_date}
            onChange={(e) => setForm((f) => ({ ...f, departure_date: e.target.value }))}
          />
          <Input
            label="Heure de départ"
            type="time"
            value={form.departure_time}
            onChange={(e) => setForm((f) => ({ ...f, departure_time: e.target.value }))}
          />
          <InputAmount
            label="Espace disponible (kg)"
            value={form.available_space}
            onChange={(v) => setForm((f) => ({ ...f, available_space: v }))}
          />
          <InputAmount
            label="Prix par kg (GNF)"
            value={form.price_per_kg}
            onChange={(v) => setForm((f) => ({ ...f, price_per_kg: v }))}
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowNewTripForm(false)}>Annuler</Button>
          <Button isLoading={saving} onClick={handleCreate}>Créer le trajet</Button>
        </div>
      </Modal>

      {trips.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Route className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun trajet planifié</p>
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((trip) => (
            <Card key={trip.id}>
              <div className="flex items-center justify-between mb-4">
                <Badge variant={trip.status === 'planned' ? 'primary' : trip.status === 'in_progress' ? 'warning' : 'success'}>
                  {trip.status === 'planned' ? 'Planifié' : trip.status === 'in_progress' ? 'En cours' : 'Terminé'}
                </Badge>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-red-50"
                  onClick={() => handleDelete(trip.id)}
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">De</p>
                  <p className="font-medium text-gray-900 truncate">{trip.from.city}</p>
                </div>
                <Route className="w-5 h-5 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-sm text-gray-500">À</p>
                  <p className="font-medium text-gray-900 truncate">{trip.to.city}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(trip.departure_date)} à {formatTime(trip.departure_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Package className="w-4 h-4" />
                  <span>{trip.available_space} kg disponibles</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="font-bold text-emerald-600">{formatCurrency(trip.price_per_kg)}/kg</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
